import { GridApiClient } from "../api/grid-api-client";
import {
  AuthenticationManager,
  AuthenticationRequest,
} from "../managers/authentication-manager";
import { AccountManager } from "../managers/account-manager";
import {
  InitAuthRequest,
  InitAuthResponse,
  CompleteAuthResponse,
  UniversalKeyPair,
  SessionSecrets,
  CompleteAuthRequestWithOtp,
  CompleteAuthAndCreateAccountRequest,
  CompleteAuthAndCreateAccountResponse,
  SignerProvider,
  RefreshSessionRequest,
  RefreshSessionResponse,
  AuthorizationError,
  GridError,
  CreateAccountResponse,
} from "../types";
import {
  GridClientContext,
  CreateAccountInput,
  CreateAccountRequest,
  EmailAccountRequest,
  SignersAccountRequest,
  AccountPolicies,
} from "../types/common";
import { ValidationUtils } from "../utils/validation";

/**
 * @hidden
 * Service handling authentication operations
 */
export class AuthService {
  private apiClient: GridApiClient;
  private authManager: AuthenticationManager;
  private accountManager: AccountManager;
  private context: GridClientContext;

  constructor(
    apiClient: GridApiClient,
    authManager: AuthenticationManager,
    accountManager: AccountManager,
    context: GridClientContext
  ) {
    this.apiClient = apiClient;
    this.authManager = authManager;
    this.accountManager = accountManager;
    this.context = context;
  }

  /**
   * Initialize authentication for an existing user.
   * This is the first step in the authentication process.
   *
   * Note: For new users, you must call createAccount first to create an account before initializing authentication. This method is only for authenticating existing users.
   *
   * @param request - Authentication request containing email, keypair, or passkey information
   * @returns Promise resolving to authentication initialization response with OTP details
   *
   * @example
   * ```typescript
   * try {
   *   const authData = await gridClient.initAuth({
   *     email: 'user@example.com'
   *   });
   *   console.log('OTP sent:', authData.otpSent);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Auth failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Authentication
   */
  async initAuth(
    request: InitAuthRequest | AuthenticationRequest
  ): Promise<InitAuthResponse> {
    // Support both old and new request formats
    const authRequest: AuthenticationRequest = {
      email: (request as any).email,
      keypair: (request as any).keypair,
      passkeyId: (request as any).passkeyId,
      provider: (request as any).provider,
      expiration: (request as any).expiration,
      smartAccountAddress: (request as any).smartAccountAddress,
    };

    const response = await this.authManager.authenticate(authRequest);

    return response;
  }

  /**
   * This is the second step in email-based authentication. Verifies the OTP
   * code received via email and establishes an authenticated session.
   *
   * Note: For new users who need to create an account, use completeAuthAndCreateAccount instead, which completes authentication and creates the account in one step.
   *
   * @param request - Complete authentication request with OTP code and user context
   * @returns Promise resolving to authentication completion response with session data
   *
   * @example
   * ```typescript
   * try {
   *   const sessionSecrets = await gridClient.generateSessionSecrets();
   *   const authResult = await gridClient.completeAuth({
   *     otpCode: '123456',
   *     user: userContext,
   *     sessionSecrets: sessionSecrets
   *   });
   *   console.log('Authenticated address:', authResult.address);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Authentication failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Authentication
   */
  async completeAuth(
    request: CompleteAuthRequestWithOtp
  ): Promise<CompleteAuthResponse> {
    const result = await this.authManager.complete(request);

    // Update account manager if auth was successful
    if (result?.data?.address) {
      const gridUserId = result.data.gridUserId;
      if (gridUserId) {
        this.accountManager.set(result.data.address, gridUserId);
      }
    }

    return result;
  }

  /**
   * Combines authentication completion with account creation for streamlined onboarding.
   * This is useful when you want to authenticate and immediately create an account.
   *
   * @param request - Complete authentication and account creation request
   * @returns Promise resolving to the created account information
   *
   * @example
   * ```typescript
   * try {
   *   const sessionSecrets = await gridClient.generateSessionSecrets();
   *   const account = await gridClient.completeAuthAndCreateAccount({
   *     otpCode: '123456',
   *     user: userContext,
   *     sessionSecrets: sessionSecrets
   *   });
   *   console.log('Account created:', account.address);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Account creation failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Authentication
   */
  async completeAuthAndCreateAccount(
    request: CompleteAuthAndCreateAccountRequest
  ): Promise<CompleteAuthAndCreateAccountResponse> {
    const result = await this.authManager.completeAndCreateAccount(request);

    // Update context with account info if available
    if (result?.data.address) {
      this.context.user.address = result.data.address;

      // Update account manager (triggers events for useAccount hook)
      const gridUserId = result.data.gridUserId;
      if (gridUserId) {
        this.accountManager.set(result.data.address, gridUserId);
      }
    }

    return result;
  }

  /**
   * Refreshes an MPC provider session to extend its lifetime
   *
   * This method refreshes authentication sessions for MPC providers like Privy,
   * updating authorization keys and tokens. The refreshed session is automatically
   * stored in the client context.
   *
   * Note: Currently only supports Privy sessions. Turnkey refresh is not yet implemented.
   *
   * @param request - Session refresh request with KMS provider and encryption key
   * @returns Promise resolving to refreshed session data
   *
   * @example
   * ```typescript
   * try {
   *   const refreshRequest: RefreshSessionRequest = {
   *     kmsPayload: {
   *       provider: 'privy',
   *       session: {
   *         userId: 'user-id',
   *         session: currentSession,
   *         token: 'jwt-token',
   *         privyAccessToken: 'access-token',
   *         refreshToken: 'refresh-token'
   *       }
   *     },
   *     encryptionPublicKey: encryptionKey
   *   };
   *   const refreshedSession = await gridClient.refreshSession(refreshRequest);
   *   console.log('Session refreshed successfully');
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Session refresh failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Authentication
   */
  async refreshSession(
    request: RefreshSessionRequest
  ): Promise<RefreshSessionResponse> {
    const response = await this.apiClient.refreshSession(request);

    // Update context with new session
    const sessionData = response.data.kmsPayload.session;

    // Extract the Session object based on provider type
    if ('session' in sessionData) {
      // This is a PrivyRefreshSession or TurnkeyRefreshSession
      this.context.user.session = (sessionData as any).session;
    }

    return response;
  }

  /**
   * Generate session secrets for all supported providers
   *
   * @returns Promise resolving to an array of secrets for different providers
   *
   * @example
   * ```typescript
   * const sessionSecrets = await gridClient.generateSessionSecrets();
   * ```
   *
   * @category Key Management
   */
  async generateSessionSecrets(): Promise<SessionSecrets> {
    return await this.authManager.generateSessionSecrets();
  }

  /**
   * @hidden
   * Generate a keypair for a specific provider
   *
   * Creates a new keypair for the specified authentication provider.
   * If no provider is specified, defaults to Privy.
   *
   * @param provider - Optional provider type ('privy', 'turnkey', 'solana', 'passkey')
   * @returns Promise resolving to a universal keypair with public and private keys
   *
   * @example
   * ```typescript
   * const keypair = await gridClient.generateKeyPair();
   *
   * const backupKeypair = await gridClient.generateKeyPair('turnkey');
   *
   * console.log(keypair.publicKey);
   * ```
   *
   * @category Key Management
   */
  async generateKeyPair(provider?: SignerProvider): Promise<UniversalKeyPair> {
    // Default to privy if no provider specified
    const targetProvider = provider || "privy";
    return await this.authManager.generateKeyPair(targetProvider);
  }

  /**
   * Creates either an email-based account (requires OTP verification)
   * or a signer-based account (immediately active).
   *
   * @param input - Account creation input (simplified) or full request object with policies
   * @returns Promise resolving to created account information
   *
   * Note: When passing AccountPolicies with signers, role defaults to 'primary' and provider defaults to 'external' if not specified.
   *
   * @example Email account
   * ```typescript
   * try {
   *   const emailAccount = await gridClient.createAccount({
   *     email: 'user@example.com'
   *   });
   *   console.log('OTP sent to:', emailAccount.email);
   * } catch (error) {
   *   if (error instanceof GridError) {
   *     console.error('Account creation failed:', error.message);
   *   }
   * }
   * ```
   *
   * @example Signer account (simplified)
   * ```typescript
   * const signerAccount = await gridClient.createAccount({
   *   signer: 'BKKjHs7kKsWFm8GKuFz3HzNKBjH4K4HhjhwxDHrRfKjj'
   * });
   * ```
   *
   * @example Configure account policies
   * ```typescript
   * const signerAccount = await gridClient.createAccount({
   *   signers: [
   *     {
   *       address: 'BKKjHs7kKsWFm8GKuFz3HzNKBjH4K4HhjhwxDHrRfKjj',
   *       permissions: ['CAN_INITIATE', 'CAN_VOTE']
   *     }
   *   ],
   *   threshold: 1,
   *   timeLock: null,
   *   adminAddress: null
   * });
   * ```
   *
   * @category Authentication
   */
  async createAccount(
    input: CreateAccountInput | CreateAccountRequest
  ): Promise<CreateAccountResponse> {
    let request: CreateAccountRequest;

    // Check if it's already a full request
    if ("type" in input) {
      request = input as CreateAccountRequest;
    } else if ("signers" in input) {
      // Convert AccountPolicies to SignersAccountRequest with defaults
      const policies = input as AccountPolicies;
      request = {
        type: 'signers',
        policies: {
          ...policies,
          signers: policies.signers.map(signer => ({
            ...signer,
            role: signer.role || 'primary',
            provider: signer.provider || 'external'
          }))
        }
      };
    } else {
      // Convert simplified input to full request
      request = this.constructAccountPayload(input as CreateAccountInput);
    }

    const response = await this.apiClient.createAccount(request);

    // Update context with account info
    if (response.data.type === "signers" && response.data.address) {
      this.context.user.address = response.data.address;
    } else if (response.data.type === "email" && response.data.email) {
      // Store email in context for later authentication
      this.context.user.email = response.data.email;
    }

    return response;
  }

  /**
   * Helper to construct account payload from simple input
   */
  private constructAccountPayload(
    input: CreateAccountInput
  ): CreateAccountRequest {
    if (!input.email && !input.signer) {
      throw new AuthorizationError(
        "Must provide either email or signer",
        "INVALID_REQUEST"
      );
    }

    if (input.email && input.signer) {
      throw new AuthorizationError(
        "Cannot provide both email and signer",
        "AMBIGUOUS_REQUEST"
      );
    }

    if (input.email) {
      if (!ValidationUtils.isValidEmail(input.email)) {
        throw new AuthorizationError("Invalid email format", "INVALID_EMAIL");
      }

      return {
        type: "email",
        email: input.email,
      } as EmailAccountRequest;
    }

    if (input.signer) {
      if (input.signer.length < 32 || input.signer.length > 44) {
        throw new AuthorizationError(
          "Invalid signer address format",
          "INVALID_REQUEST"
        );
      }

      const accountPolicies: AccountPolicies = {
        signers: [
          {
            address: input.signer,
            role: "primary",
            permissions: ["CAN_INITIATE", "CAN_EXECUTE", "CAN_VOTE"],
            provider: "external",
          },
        ],
        threshold: 1,
        timeLock: null,
        adminAddress: null,
      };

      return {
        type: "signers",
        policies: accountPolicies,
        gridUserId: null,
        memo: undefined,
      } as SignersAccountRequest;
    }

    throw new AuthorizationError("Invalid input parameters", "INVALID_REQUEST");
  }
}
